import React, { useState } from "react";
import { useReveal } from "@/lib/useReveal";
import { Plus, Minus } from "lucide-react";

const FAQS = [
  {
    q: "Can I see it work before I pay?",
    a: "Yes. Set up your brand and run one real scan on your own business - it runs start to finish and you see the actual results, not a demo. To open the sources behind each result and save leads, you upgrade. One plan: 15 scans a month, up to 15 keywords each.",
  },
  {
    q: "How is this different from Sales Navigator or Apollo?",
    a: "Those tools give you contact databases and export lists. Leadmato watches the live social web for expressed buying intent - actual sentences like 'looking for a CRM under 2k'. It's a different job entirely: not \"a lead list\", but \"who is telling the internet they need this today?\".",
  },
  {
    q: "What platforms does Leadmato scan?",
    a: "Reddit (posts and comments), public forums, LinkedIn, X (Twitter), YouTube, Instagram, Facebook, TikTok, plus open web and news pages. Every scan pre-selects the platforms that fit your lens, but you can tick or untick any of them before you run - so you're always in control of where we look. We keep adding sources based on which platforms your buyers hang out on - Indian founders often ask us for niche forums, we plug them in.",
  },
  {
    q: "Do I need any technical setup?",
    a: "No. Paste your website URL, and we auto-generate your brand profile, ICPs, and buyer keywords. If you want to edit them (you should - five minutes of tuning triples scan quality) you can. But there's a zero-setup path too.",
  },
  {
    q: "What data do you store? Is my brand info safe?",
    a: "We store only what's needed to run scans: your brand profile, keywords, and the results you save. We never sell or share your data. We're DPDP-compliant and hosted on infrastructure with encryption at rest and in transit.",
  },
  {
    q: "Can I cancel or get a refund?",
    a: "Cancel anytime - no lock-in, no cancellation fee. And if a scan comes back with nothing, we refund that scan automatically.",
  },
];

function FAQItem({ q, a, i, open, onToggle }) {
  return (
    <div
      className={`border border-lm-border rounded-xl bg-lm-surface overflow-hidden transition-shadow ${open ? "shadow-[0_8px_24px_-16px_rgba(31,74,56,0.3)]" : ""}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 md:px-6 py-4 md:py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display font-semibold text-lm-text pr-4">{q}</span>
        <span className={`shrink-0 w-8 h-8 rounded-full ${open ? "bg-lm-primary text-white" : "bg-lm-tint text-lm-primary"} flex items-center justify-center transition-colors`}>
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0 text-lm-text2 leading-relaxed">
            {a}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  const [headRef, headVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal(0.1);

  return (
    <section id="faq" className="max-w-4xl mx-auto px-5 md:px-10 py-20 md:py-24 lm-cv">
      <div
        ref={headRef}
        className={`max-w-2xl mb-10 lm-reveal ${headVisible ? "is-visible" : ""}`}
      >
        <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
          FAQ
        </div>
        <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
          Answers before you ask.
        </h2>
      </div>
      <div ref={gridRef} className={`flex flex-col gap-3 lm-stagger`}>
        {FAQS.map((f, i) => (
          <div
            key={f.q}
            style={{ "--i": i }}
            className={`lm-reveal ${gridVisible ? "is-visible" : ""}`}
          >
            <FAQItem
              {...f}
              i={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
